import React from "react";
import { ArrowRight, Flag } from "lucide-react";
import Logo from "@/components/Logo";

// ============================================================
// MOCKUP B — High-Fidelity Eclectic Scorecard
// The product itself is the hero. A beautifully rendered 18-hole
// eclectic card with par-aware tinting, gross/net badges, and a
// "your best round" callout.
// ============================================================

const HOLES = [
  { p: 4, b: 3 }, { p: 4, b: 4 }, { p: 5, b: 4 }, { p: 3, b: 3 },
  { p: 4, b: 4 }, { p: 5, b: 5 }, { p: 4, b: 3 }, { p: 3, b: 3 }, { p: 4, b: 4 },
  { p: 4, b: 4 }, { p: 4, b: 3 }, { p: 5, b: 5 }, { p: 3, b: 2 },
  { p: 4, b: 4 }, { p: 5, b: 4 }, { p: 4, b: 4 }, { p: 3, b: 3 }, { p: 4, b: 3 },
];

export default function HeroMockup2() {
  const eclectic = HOLES.reduce((a, h) => a + h.b, 0);
  const par = HOLES.reduce((a, h) => a + h.p, 0);
  return (
    <div className="min-h-screen bg-app">
      <header className="border-b border-line bg-app/80 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto max-w-6xl px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo variant="icon" size={28} />
            <span className="text-[15px] font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </div>
          <span className="text-[12px] text-ink-soft">Mockup B · Scorecard hero</span>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-5 sm:px-8">
        <section className="grid lg:grid-cols-12 gap-10 lg:gap-14 pt-12 sm:pt-20 pb-16 items-center">
          <div className="lg:col-span-5">
            <p className="ge-eyebrow mb-4">Golf Eclectic · multi-round format</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] text-ink leading-[1.05]">
              Your best on <span className="text-accent">every hole.</span>
            </h1>
            <p className="text-[17px] sm:text-lg text-ink-muted mt-6 leading-relaxed">
              An eclectic competition keeps the lowest score you’ve made on each hole
              across every round you play. Your perfect round, built over a season.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-2.5">
              <button className="ge-btn-primary !px-5 !py-3 !text-[14.5px] justify-center">Sign in <ArrowRight className="w-4 h-4" /></button>
              <button className="ge-btn-outline !px-5 !py-3 !text-[14.5px] justify-center">Continue with Google</button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="ge-card p-5 shadow-sm">
              {/* Card header */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10.5px] uppercase tracking-[0.14em] text-accent font-semibold">Your eclectic</p>
                  <p className="text-[17px] font-semibold tracking-tight text-ink mt-0.5">Alex Mercer · Spring Eclectic</p>
                  <p className="text-[12.5px] text-ink-muted mt-0.5">After 4 of 10 rounds · Knole Park, White tees</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-ink-soft font-semibold">Net eclectic</p>
                  <p className="text-3xl font-semibold tabular-nums text-ink">{eclectic - 6}</p>
                  <p className="text-[11px] text-accent font-medium">−{par - eclectic + 6} to par</p>
                </div>
              </div>

              {/* Holes grid */}
              <div className="mt-4 grid grid-cols-9 gap-1">
                {HOLES.map((h, i) => {
                  const d = h.b - h.p;
                  const cls = d < -1 ? "bg-accent text-white border-accent"
                    : d === -1 ? "bg-accent-tint text-accent-deep border-accent/30"
                    : d === 0 ? "bg-white text-ink border-line"
                    : "bg-surface-2 text-ink-muted border-line";
                  return (
                    <div key={i} className={`rounded-md py-1.5 text-center border ${cls}`}>
                      <div className="text-[9px] opacity-70 font-mono tabular-nums">{i + 1}</div>
                      <div className="text-[14px] font-semibold tabular-nums">{h.b}</div>
                      <div className="text-[8px] opacity-60">par {h.p}</div>
                    </div>
                  );
                })}
              </div>

              {/* Round chips */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-[10.5px] uppercase tracking-[0.12em] text-ink-soft font-semibold">From</span>
                {[
                  { r: "R1", g: 84 },
                  { r: "R2", g: 81, hl: true },
                  { r: "R3", g: 88 },
                  { r: "R4", g: 79, hl: true },
                ].map((rd) => (
                  <span key={rd.r} className={`text-[11px] px-2 py-0.5 rounded-full font-mono tabular-nums ${rd.hl ? "bg-accent-tint-2 text-accent-deep" : "bg-surface-2 text-ink-muted"}`}>
                    {rd.r} · {rd.g}
                  </span>
                ))}
              </div>

              {/* Position banner */}
              <div className="mt-4 flex items-center justify-between bg-ink rounded-lg px-4 py-3 text-white">
                <div className="flex items-center gap-2.5">
                  <Flag className="w-4 h-4 text-accent-soft" />
                  <span className="text-[13px]">You’re currently <b className="text-white">4th of 18</b> in the field</span>
                </div>
                <ArrowRight className="w-4 h-4 opacity-60" />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
